/* Fiches produit — interactions du bloc d'achat.

   Portée locale : une page peut poser plusieurs fois la section (gabarits
   comparés, aperçus), et deux exécutions d'un même `class` en portée globale
   lèvent « already been declared ». */
(() => {
  const euro = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  class LtbProduct {
    constructor(root) {
      this.root = root;
      this.qtyInput = root.querySelector('[data-pdp-qty-input]');
      this.ctaText = root.querySelector('[data-pdp-cta-text]');
      this.cta = root.querySelector('[data-pdp-cta]');
      this.variantInput = root.querySelector('[data-pdp-variant-input]');
      this.priceEl = root.querySelector('[data-pdp-price]');
      this.chips = [...root.querySelectorAll('[data-variant-id]')];
      // Tout ce qui se dérive du prix : tarif abonné, échéance Scalapay,
      // mention au poids. Rendus une fois par Liquid, ils restaient figés
      // sur la première variante.
      this.subEls = [...root.querySelectorAll('[data-pdp-sub]')];
      this.thirdEl = root.querySelector('[data-pdp-third]');
      this.unitEl = root.querySelector('[data-pdp-unit]');
      // Taux de remise abonné du produit : 15, 10 ou 0. Posé par Liquid
      // depuis la même source que les remises automatiques de la caisse.
      this.subRate = Number(root.dataset.subRate || 0);

      const active = this.chips.find((c) => c.getAttribute('aria-pressed') === 'true');
      this.unitPrice = active ? Number(active.dataset.variantPrice) : this.readInitialPrice();

      this.bindPill();
      this.bindQuantity();
      this.bindVariants();
    }

    readInitialPrice() {
      // Pas de chip : le prix de départ se lit sur le premier variant du form.
      const el = this.root.querySelector('[data-variant-price]');
      return el ? Number(el.dataset.variantPrice) : 0;
    }

    // La galerie du thème s'adresse par identifiant de visuel. On vérifie
    // que la diapositive existe avant d'appeler : `setActiveMedia` suppose
    // qu'elle est là et lèverait sur un identifiant inconnu.
    montrerMedia(mediaId) {
      const galerie = this.root.querySelector('media-gallery');
      if (!galerie || typeof galerie.setActiveMedia !== 'function') return;
      if (!galerie.querySelector(`[data-media-id="${mediaId}"]`)) return;
      galerie.setActiveMedia(mediaId, false);
    }

    bindPill() {
      const pill = this.root.querySelector('[data-pdp-pill]');
      if (!pill) return;
      const panel = this.root.querySelector('#' + CSS.escape(pill.getAttribute('aria-controls')));
      if (!panel) return;
      pill.addEventListener('click', () => {
        const open = pill.getAttribute('aria-expanded') === 'true';
        pill.setAttribute('aria-expanded', String(!open));
        panel.hidden = open;
      });
    }

    bindQuantity() {
      if (!this.qtyInput) return;
      this.root.querySelectorAll('[data-qty-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = Number(btn.dataset.qtyStep);
          const next = Math.max(1, Number(this.qtyInput.value || 1) + step);
          this.qtyInput.value = next;
          this.refreshCta();
        });
      });
      this.qtyInput.addEventListener('change', () => {
        if (Number(this.qtyInput.value) < 1) this.qtyInput.value = 1;
        this.refreshCta();
      });
    }

    bindVariants() {
      this.chips.forEach((chip) => {
        if (chip.disabled) return;
        chip.addEventListener('click', () => {
          this.chips.forEach((c) => c.setAttribute('aria-pressed', 'false'));
          chip.setAttribute('aria-pressed', 'true');
          this.unitPrice = Number(chip.dataset.variantPrice);
          if (this.variantInput) this.variantInput.value = chip.dataset.variantId;
          if (this.priceEl) this.priceEl.textContent = euro(this.unitPrice);
          this.refreshDerived(chip.dataset.variantUnit);
          // Le conditionnement a son propre visuel : la galerie le suit.
          if (chip.dataset.variantMedia) this.montrerMedia(chip.dataset.variantMedia);
          this.refreshCta();
        });
      });
    }

    /* Le tarif abonné suit la variante ET le taux du produit. `Math.round`
       reproduit le `| times: facteur | divided_by: 100.0 | round` du Liquid,
       et `Math.floor` le `| divided_by: 3`, qui est une division entière :
       les deux rendus restent identiques. */
    refreshDerived(unitNote) {
      const sub = Math.round((this.unitPrice * (100 - this.subRate)) / 100);
      this.subEls.forEach((el) => { el.textContent = euro(sub); });
      if (this.thirdEl) this.thirdEl.textContent = euro(Math.floor(this.unitPrice / 3));
      // Une variante sans mention laisse celle du gabarit en place plutôt
      // que d'afficher une ligne vide.
      if (this.unitEl && unitNote) this.unitEl.textContent = unitNote;
    }

    refreshCta() {
      if (!this.ctaText || !this.unitPrice) return;
      const qty = Math.max(1, Number(this.qtyInput ? this.qtyInput.value : 1));
      const prefix = this.cta ? this.cta.dataset.labelPrefix || '' : '';
      this.ctaText.textContent = prefix + euro(this.unitPrice * qty);
    }
  }

  /* Barre d'achat collée, mobile. Elle ne calcule rien : elle recopie l'état
     du bloc d'achat, et son bouton relaie le clic au vrai CTA. Le gabarit Box
     lui envoie `ltb:offre` quand l'offre change, parce que le prix du jour
     (20,23 €) n'est alors pas celui affiché en haut de fiche (28,90 €). */
  class BarreMobile {
    constructor(barre, racine) {
      this.barre = barre;
      this.racine = racine;
      this.prixEl = barre.querySelector('[data-barre-prix]');
      this.noteEl = barre.querySelector('[data-barre-note]');
      this.ctaEl = barre.querySelector('[data-barre-cta]');

      this.cta = racine.querySelector('[data-pdp-cta]');
      this.form = racine.querySelector('.ltb-pdp__form');
      this.prixSource = racine.querySelector('[data-pdp-price]');
      this.subSource = racine.querySelector('[data-pdp-sub]');
      this.ctaTexte = racine.querySelector('[data-pdp-cta-text]');
      this.offre = null;

      if (!this.form || !this.cta) return;
      this.brancher();
      this.rendre();
    }

    brancher() {
      this.ctaEl.addEventListener('click', () => this.cta.click());

      document.addEventListener('ltb:offre', (e) => {
        this.offre = e.detail || null;
        this.rendre();
      });

      // Le libellé du CTA change avec la quantité et la variante : on suit.
      const suivre = () => window.setTimeout(() => this.rendre(), 0);
      this.racine.querySelectorAll('[data-qty-step], [data-pdp-qty-input], [data-variant-id]')
        .forEach((el) => {
          el.addEventListener('click', suivre);
          el.addEventListener('change', suivre);
        });

      // La barre paraît dès que le bloc d'achat quitte l'écran par le haut.
      // Une simple lecture au défilement plutôt qu'un IntersectionObserver :
      // l'observateur ne se déclenchait pas sur iOS, et rien ici ne justifie
      // un mécanisme qu'on ne peut pas déboguer à distance.
      let enAttente = false;
      this.majVisibilite = () => {
        const r = this.form.getBoundingClientRect();
        const partiHaut = r.bottom <= 0;
        this.barre.classList.toggle('is-visible', partiHaut);
        this.barre.setAttribute('aria-hidden', partiHaut ? 'false' : 'true');
        this.ctaEl.tabIndex = partiHaut ? 0 : -1;
      };
      const auDefilement = () => {
        if (enAttente) return;
        enAttente = true;
        window.requestAnimationFrame(() => {
          enAttente = false;
          this.majVisibilite();
        });
      };
      window.addEventListener('scroll', auDefilement, { passive: true });
      window.addEventListener('resize', auDefilement, { passive: true });
      this.majVisibilite();
    }

    rendre() {
      if (this.offre && this.offre.prix) {
        this.prixEl.textContent = euro(this.offre.prix);
        this.noteEl.textContent = this.offre.note || '';
      } else {
        this.prixEl.textContent = this.prixSource ? this.prixSource.textContent.trim() : '';
        this.noteEl.textContent = this.subSource
          ? `${this.subSource.textContent.trim()} abonné·e`
          : '';
      }
      // La barre reprend l'etat du bouton principal : sur un produit epuise
      // elle affichait « Épuisé » tout en restant cliquable. Le clic ne faisait
      // rien — il est relaye a un bouton desactive — mais rien ne le disait, et
      // c'est l'affordance d'achat principale sur mobile.
      this.ctaEl.disabled = this.cta.disabled;
      const libelle = this.ctaTexte ? this.ctaTexte.textContent.trim() : '';
      // « Ajouter au panier · 28,90 € » : le prix est déjà à gauche de la barre.
      this.ctaEl.textContent = libelle.startsWith('Continuer')
        ? 'Continuer'
        : libelle.split(' · ')[0] || 'Ajouter au panier';
    }
  }

  /* En apercu de theme, Shopify pose sa propre barre fixe en bas de l'ecran
     (#PBarNextFrameWrapper, ~68 px) et elle se peint par-dessus tout, quel que
     soit le z-index : la barre d'achat s'y cachait entierement, et la feuille
     du tunnel y perdait son dernier lien. On publie sa hauteur sur la racine,
     et ce qui se colle au bas de l'ecran s'en ecarte d'autant. Hors apercu
     l'element n'existe pas : la variable vaut 0 et rien ne se decale. */
  const mesurerBarreDApercu = () => {
    const caler = () => {
      const apercu = document.getElementById('PBarNextFrameWrapper');
      const h = apercu ? Math.round(apercu.getBoundingClientRect().height) : 0;
      document.documentElement.style.setProperty('--ltb-barre-bas', `${h}px`);
      return h > 0;
    };
    window.addEventListener('resize', caler, { passive: true });
    if (caler()) return;
    // Shopify injecte sa barre apres le chargement : on l'attend, puis on
    // arrete d'observer des qu'elle est la.
    const obs = new MutationObserver(() => { if (caler()) obs.disconnect(); });
    obs.observe(document.body, { childList: true });
    window.setTimeout(() => obs.disconnect(), 15000);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('.ltb-pdp__inner').forEach((root) => {
      if (root.dataset.ltbReady) return;
      root.dataset.ltbReady = '1';
      new LtbProduct(root);

      const barre = document.querySelector('[data-ltb-barre]');
      if (barre && !barre.dataset.pret) {
        barre.dataset.pret = '1';
        // Un ancetre transforme priverait ce `position: fixed` de son
        // ancrage a la fenetre : la barre se poserait au milieu de la page
        // au lieu du bas de l'ecran. Le body est le seul parent sur.
        if (barre.parentElement !== document.body) document.body.appendChild(barre);
        new BarreMobile(barre, root);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { mesurerBarreDApercu(); init(); });
  } else {
    mesurerBarreDApercu();
    init();
  }

  // L'éditeur de thème remonte la section : on ré-initialise ce qu'il réinjecte.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
